/**
 * Accreditation Rules Engine
 * Implements NUC accreditation decision rules for different programme types
 * Based on official NUC guidelines for UG/Affiliate, PG, and ODL programmes
 */

/**
 * Calculate accreditation recommendation based on programme type and template scores
 * @param {Object} templateScores - Object containing scores for each template (keyed by templateId)
 * @param {Array} templates - Array of template objects with items
 * @param {string} programmeType - Programme type: 'undergraduate', 'postgraduate', or 'odl'
 * @returns {Object} - {recommendation: string, reason: string, details: Object, coreAreas: Array}
 */
export const calculateAccreditationRecommendation = (templateScores, templates, programmeType = 'undergraduate') => {
  // Filter only core templates (type === "core")
  const coreTemplates = templates.filter(t => t.type === "core");

  if (coreTemplates.length === 0) {
    return {
      recommendation: 'INSUFFICIENT DATA',
      reason: 'No core templates found for evaluation.',
      details: {
        coreAreas: [],
        rule: 'NO_CORE_TEMPLATES'
      }
    };
  }

  // Build core areas array from actual core templates
  const coreAreas = coreTemplates.map(template => {
    const templateScore = templateScores[template._id];
    const earned = templateScore?.metadata?.totalScore || 0;
    const max = templateScore?.metadata?.maxPossibleScore || 0;
    const percentage = max > 0 ? (earned / max) * 100 : 0;

    return {
      name: template.name || 'Unnamed Area',
      templateId: template._id,
      score: earned,
      max: max,
      percentage: percentage,
      type: template.type,
    };
  });

  // Route to appropriate rule set based on programme type
  const normalizedType = programmeType?.toLowerCase() || 'undergraduate';

  if (normalizedType.includes('postgraduate') || normalizedType === 'pg') {
    return applyPGRules(coreAreas, coreTemplates.length);
  } else if (normalizedType.includes('odl') || normalizedType.includes('distance')) {
    return applyODLRules(coreAreas, coreTemplates.length);
  } else {
    // Default to UG rules (also applies to Affiliate programmes)
    return applyUGRules(coreAreas, coreTemplates.length);
  }
};

/**
 * Apply Undergraduate and Affiliate Programme Accreditation Rules
 *
 * CONDITION 1 - FULL ACCREDITATION:
 * - All core areas >= 70%
 * - Exception: If 2-3 areas >= 70% but 1 area is 60-69% → INTERIM
 *
 * CONDITION 2 - INTERIM ACCREDITATION:
 * - All core areas between 60-69%
 * - Exception: If 2-3 areas are 60-69% but 1 area < 60% → DENIED
 *
 * CONDITION 3 - DENIED ACCREDITATION:
 * - All core areas < 60% (50-59%)
 */
const applyUGRules = (coreAreas, totalCoreAreas) => {
  const areas70Plus = coreAreas.filter(a => a.percentage >= 70);
  const areas60to69 = coreAreas.filter(a => a.percentage >= 60 && a.percentage < 70);
  const areasBelow60 = coreAreas.filter(a => a.percentage < 60);

  // CONDITION 1: Full Accreditation - All core areas >= 70%
  if (areas70Plus.length === totalCoreAreas) {
    return {
      recommendation: 'FULL ACCREDITATION',
      reason: `All ${totalCoreAreas} core areas scored 70% or above.`,
      details: {
        areas70Plus: areas70Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areas60to69: [],
        areasBelow60: [],
        rule: 'UG_FULL_ALL_CORES_70_PLUS',
        coreAreas: coreAreas
      }
    };
  }

  // CONDITION 1 Exception: 2-3 areas >= 70%, 1+ area in 60-69%, no areas < 60% → Interim
  if (areas70Plus.length >= 2 && areas60to69.length >= 1 && areasBelow60.length === 0) {
    return {
      recommendation: 'INTERIM ACCREDITATION',
      reason: `${areas70Plus.length} core area(s) achieved 70% or above, but ${areas60to69.length} area(s) scored between 60-69%: ${areas60to69.map(a => a.name).join(', ')}.`,
      details: {
        areas70Plus: areas70Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areas60to69: areas60to69.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areasBelow60: [],
        rule: 'UG_INTERIM_EXCEPTION_FROM_FULL',
        coreAreas: coreAreas
      }
    };
  }

  // CONDITION 2: Interim Accreditation - All core areas in 60-69%
  if (areas60to69.length === totalCoreAreas) {
    return {
      recommendation: 'INTERIM ACCREDITATION',
      reason: `All ${totalCoreAreas} core areas scored between 60% and 69%.`,
      details: {
        areas70Plus: [],
        areas60to69: areas60to69.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areasBelow60: [],
        rule: 'UG_INTERIM_ALL_60_TO_69',
        coreAreas: coreAreas
      }
    };
  }

  // CONDITION 2 Exception: 2+ areas in 60-69%, 1+ area < 60% → Denied
  if (areas60to69.length >= 2 && areasBelow60.length >= 1) {
    return {
      recommendation: 'DENIED ACCREDITATION',
      reason: `${areasBelow60.length} core area(s) scored below 60%: ${areasBelow60.map(a => a.name).join(', ')}.`,
      details: {
        areas70Plus: areas70Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areas60to69: areas60to69.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areasBelow60: areasBelow60.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        rule: 'UG_DENIED_EXCEPTION_FROM_INTERIM',
        coreAreas: coreAreas
      }
    };
  }

  // CONDITION 3: Denied Accreditation - All core areas < 60%
  if (areasBelow60.length === totalCoreAreas) {
    return {
      recommendation: 'DENIED ACCREDITATION',
      reason: `All ${totalCoreAreas} core areas scored below 60%.`,
      details: {
        areas70Plus: [],
        areas60to69: [],
        areasBelow60: areasBelow60.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        rule: 'UG_DENIED_ALL_BELOW_60',
        coreAreas: coreAreas
      }
    };
  }

  // Fallback: If any area is below 60%, deny
  if (areasBelow60.length > 0) {
    return {
      recommendation: 'DENIED ACCREDITATION',
      reason: `${areasBelow60.length} core area(s) scored below 60%: ${areasBelow60.map(a => a.name).join(', ')}.`,
      details: {
        areas70Plus: areas70Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areas60to69: areas60to69.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areasBelow60: areasBelow60.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        rule: 'UG_DENIED_DEFAULT',
        coreAreas: coreAreas
      }
    };
  }

  // Default to interim if all areas >= 60%
  return {
    recommendation: 'INTERIM ACCREDITATION',
    reason: 'Based on overall core area performance.',
    details: {
      areas70Plus: areas70Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
      areas60to69: areas60to69.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
      areasBelow60: areasBelow60.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
      rule: 'UG_INTERIM_DEFAULT',
      coreAreas: coreAreas
    }
  };
};

/**
 * Apply Postgraduate Programme Accreditation Rules
 *
 * CONDITION 1 - ACCREDITED:
 * - All core areas >= 60%
 * - Exception: If 2-3 areas >= 60% but 1+ area < 60% → DENIED
 *
 * DENIED ACCREDITATION:
 * - All core areas < 60%
 */
const applyPGRules = (coreAreas, totalCoreAreas) => {
  const areas60Plus = coreAreas.filter(a => a.percentage >= 60);
  const areasBelow60 = coreAreas.filter(a => a.percentage < 60);

  // CONDITION 1: Accredited - All core areas >= 60%
  if (areas60Plus.length === totalCoreAreas) {
    return {
      recommendation: 'ACCREDITED',
      reason: `All ${totalCoreAreas} core areas scored 60% or above.`,
      details: {
        areas60Plus: areas60Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areasBelow60: [],
        rule: 'PG_ACCREDITED_ALL_60_PLUS',
        coreAreas: coreAreas
      }
    };
  }

  // CONDITION 1 Exception: 2+ areas >= 60%, 1+ area < 60% → Denied
  if (areas60Plus.length >= 2 && areasBelow60.length >= 1) {
    return {
      recommendation: 'DENIED ACCREDITATION',
      reason: `${areasBelow60.length} core area(s) scored below 60%: ${areasBelow60.map(a => a.name).join(', ')}.`,
      details: {
        areas60Plus: areas60Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        areasBelow60: areasBelow60.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        rule: 'PG_DENIED_EXCEPTION',
        coreAreas: coreAreas
      }
    };
  }

  // Denied Accreditation - All core areas < 60%
  if (areasBelow60.length === totalCoreAreas) {
    return {
      recommendation: 'DENIED ACCREDITATION',
      reason: `All ${totalCoreAreas} core areas scored below 60%.`,
      details: {
        areas60Plus: [],
        areasBelow60: areasBelow60.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
        rule: 'PG_DENIED_ALL_BELOW_60',
        coreAreas: coreAreas
      }
    };
  }

  // Fallback: If any area is below 60%, deny
  return {
    recommendation: 'DENIED ACCREDITATION',
    reason: 'One or more core areas did not meet the minimum 60% threshold.',
    details: {
      areas60Plus: areas60Plus.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
      areasBelow60: areasBelow60.map(a => `${a.name}: ${a.percentage.toFixed(1)}%`),
      rule: 'PG_DENIED_DEFAULT',
      coreAreas: coreAreas
    }
  };
};

/**
 * Apply Open & Distance Learning (ODL) Accreditation Rules
 * Same rules as UG/Affiliate programmes
 */
const applyODLRules = (coreAreas, totalCoreAreas) => {
  return applyUGRules(coreAreas, totalCoreAreas);
};

/**
 * Get a human-readable explanation of the rule that was applied
 */
export const getRuleExplanation = (programmeType, ruleCode) => {
  const explanations = {
    // UG/Affiliate/ODL Rules
    UG_FULL_ALL_CORES_70_PLUS: 'All core areas achieved 70% or above.',
    UG_INTERIM_EXCEPTION_FROM_FULL: 'Most core areas achieved 70% or above, but one or more areas scored between 60-69%.',
    UG_INTERIM_ALL_60_TO_69: 'All core areas scored between 60% and 69%.',
    UG_DENIED_EXCEPTION_FROM_INTERIM: 'One or more core areas scored below 60%.',
    UG_DENIED_ALL_BELOW_60: 'All core areas scored below 60%.',
    UG_DENIED_DEFAULT: 'One or more core areas did not meet the minimum threshold.',
    UG_INTERIM_DEFAULT: 'Based on overall core area performance.',

    // PG Rules
    PG_ACCREDITED_ALL_60_PLUS: 'All core areas achieved 60% or above.',
    PG_DENIED_EXCEPTION: 'One or more core areas scored below 60%.',
    PG_DENIED_ALL_BELOW_60: 'All core areas scored below 60%.',
    PG_DENIED_DEFAULT: 'One or more core areas did not meet the minimum 60% threshold.',

    // Special
    NO_CORE_TEMPLATES: 'No core templates available for evaluation.',
  };

  return explanations[ruleCode] || 'Evaluation based on NUC accreditation guidelines.';
};

/**
 * Validate if programme type is supported
 */
export const isValidProgrammeType = (programmeType) => {
  const validTypes = ['undergraduate', 'postgraduate', 'odl', 'ug', 'pg', 'affiliate', 'open and distance learning'];
  return validTypes.includes(programmeType?.toLowerCase());
};

/**
 * Get programme type display name
 */
export const getProgrammeTypeDisplayName = (programmeType) => {
  const normalizedType = programmeType?.toLowerCase() || 'undergraduate';

  if (normalizedType.includes('postgraduate') || normalizedType === 'pg') {
    return 'Postgraduate';
  } else if (normalizedType.includes('odl') || normalizedType.includes('distance')) {
    return 'Open & Distance Learning';
  } else if (normalizedType.includes('affiliate')) {
    return 'Affiliate Programme';
  } else {
    return 'Undergraduate';
  }
};

export default {
  calculateAccreditationRecommendation,
  getRuleExplanation,
  isValidProgrammeType,
  getProgrammeTypeDisplayName,
};
